/**
 * Client-side business intelligence metrics. See TECHNICAL_DOCUMENTATION.txt (Gross margin per SKU, Dead stock, Reorder).
 */

import {
  daysBetweenOrderAndDelivery,
  REORDER_SETUP_CTA,
  resolveLeadTimeDays,
  type LeadTimeSource,
} from "./reorderLeadTime";

export type SaleLine = {
  item_id: string | null;
  quantity: number | null;
  final_amount: number | null;
  total_price: number | null;
  unit_price: number | null;
  created_at: string;
  voided_at?: string | null;
};

export type InvRow = {
  id: string;
  sku: string;
  name: string;
  current_stock: number;
  buying_price: number | null;
  selling_price: number | null;
  branch_id?: string | null;
};

function saleLineAmount(s: SaleLine): number {
  const amt = s.final_amount ?? s.total_price ?? (s.quantity && s.unit_price ? s.quantity * s.unit_price : 0);
  return Number(amt || 0);
}

export type SkuProfitRow = {
  item_id: string;
  sku: string;
  name: string;
  current_stock: number;
  buying_price_used: number;
  units_sold: number;
  revenue: number;
  cogs_estimate: number;
  gross_profit: number;
  margin_pct: number | null;
};

/** Last 30d gross margin per SKU using current buying_price × quantity sold (approximation). */
export function computeProfitPerSku(sales: SaleLine[], inventory: InvRow[], sinceIso: string): SkuProfitRow[] {
  const invById = new Map(inventory.map((i) => [i.id, i]));
  const agg = new Map<
    string,
    { sku: string; name: string; units: number; revenue: number; cogs: number }
  >();

  const since = new Date(sinceIso).getTime();
  for (const s of sales) {
    if (s.voided_at) continue;
    if (!s.item_id) continue;
    const t = new Date(s.created_at).getTime();
    if (t < since) continue;
    const inv = invById.get(s.item_id);
    if (!inv) continue;
    const qty = Math.max(0, s.quantity ?? 0);
    const rev = saleLineAmount(s);
    const bp = Number(inv.buying_price ?? 0);
    const cogs = qty * bp;
    const cur = agg.get(s.item_id) ?? { sku: inv.sku, name: inv.name, units: 0, revenue: 0, cogs: 0 };
    cur.units += qty;
    cur.revenue += rev;
    cur.cogs += cogs;
    agg.set(s.item_id, cur);
  }

  const rows: SkuProfitRow[] = [];
  for (const [itemId, v] of agg) {
    const inv = invById.get(itemId);
    const currentStock = Number(inv?.current_stock ?? 0);
    const buyingPriceUsed = Number(inv?.buying_price ?? 0);
    const gp = v.revenue - v.cogs;
    const margin_pct = v.revenue > 0 ? (gp / v.revenue) * 100 : null;
    rows.push({
      item_id: itemId,
      sku: v.sku,
      name: v.name,
      current_stock: currentStock,
      buying_price_used: buyingPriceUsed,
      units_sold: v.units,
      revenue: v.revenue,
      cogs_estimate: v.cogs,
      gross_profit: gp,
      margin_pct,
    });
  }
  rows.sort((a, b) => b.gross_profit - a.gross_profit);
  return rows;
}

export type DeadStockRow = {
  item_id: string;
  sku: string;
  name: string;
  current_stock: number;
  cash_tied: number;
  days_without_sale: number;
  suggested_discount_pct: number;
  recovery_scenario_kwacha: number;
};

export function computeDeadStockLiquidation(
  inventory: InvRow[],
  sales: SaleLine[],
  opts: { staleDays: number; discountPct: number }
): DeadStockRow[] {
  const lastSaleMs = new Map<string, number>();
  for (const s of sales) {
    if (s.voided_at || !s.item_id) continue;
    const t = new Date(s.created_at).getTime();
    const prev = lastSaleMs.get(s.item_id);
    if (prev == null || t > prev) lastSaleMs.set(s.item_id, t);
  }

  const now = Date.now();
  const rows: DeadStockRow[] = [];
  for (const inv of inventory) {
    if (inv.current_stock <= 0) continue;
    const last = lastSaleMs.get(inv.id);
    const daysWithout = last == null ? 9999 : Math.floor((now - last) / (24 * 60 * 60 * 1000));
    if (daysWithout < opts.staleDays) continue;
    const bp = Number(inv.buying_price ?? 0);
    const cashTied = inv.current_stock * bp;
    if (cashTied <= 0) continue;
    const recovery = Math.round(cashTied * (1 - opts.discountPct / 100));
    rows.push({
      item_id: inv.id,
      sku: inv.sku,
      name: inv.name,
      current_stock: inv.current_stock,
      cash_tied: Math.round(cashTied),
      days_without_sale: last == null ? opts.staleDays : daysWithout,
      suggested_discount_pct: opts.discountPct,
      recovery_scenario_kwacha: recovery,
    });
  }
  rows.sort((a, b) => b.cash_tied - a.cash_tied);
  return rows;
}

export type ReorderRow = {
  item_id: string;
  sku: string;
  name: string;
  current_stock: number;
  units_per_week: number;
  days_of_cover: number | null;
  lead_days: number | null;
  lead_source: LeadTimeSource;
  has_reorder_advice: boolean;
  is_urgent: boolean;
  message: string;
};

type PoRow = { id: string; supplier_id: string; order_date: string };
type DelRow = {
  purchase_order_id: string;
  delivery_date: string;
  received_at: string | null;
  status: string;
};
type PolRow = { purchase_order_id: string; inventory_id: string | null };
type SupplierRow = { id: string; default_lead_time_days: number | null };

/** Per-SKU lead-time samples from delivered POs in Transport & Logistics. */
export function buildSkuLeadSamples(pos: PoRow[], deliveries: DelRow[], pols: PolRow[]): Map<string, number[]> {
  const poById = new Map(pos.map((p) => [p.id, p]));
  const invIdsByPo = new Map<string, Set<string>>();
  for (const line of pols) {
    if (!line.inventory_id) continue;
    const set = invIdsByPo.get(line.purchase_order_id) ?? new Set<string>();
    set.add(line.inventory_id);
    invIdsByPo.set(line.purchase_order_id, set);
  }

  const bySku = new Map<string, number[]>();
  for (const d of deliveries) {
    if (d.status !== "delivered" && !d.received_at) continue;
    const po = poById.get(d.purchase_order_id);
    if (!po) continue;
    const days = daysBetweenOrderAndDelivery(po.order_date, d.delivery_date, d.received_at);
    if (days <= 0 || days > 120) continue;
    const invIds = invIdsByPo.get(d.purchase_order_id);
    if (!invIds) continue;
    for (const invId of invIds) {
      const arr = bySku.get(invId) ?? [];
      arr.push(days);
      bySku.set(invId, arr);
    }
  }
  return bySku;
}

/** Latest supplier_id per inventory from purchase order lines (by max order_date). */
export function latestSupplierByInventoryId(pos: PoRow[], pols: PolRow[]): Map<string, string> {
  const poById = new Map(pos.map((p) => [p.id, p]));
  const best = new Map<string, { orderDate: string; supplier_id: string }>();
  for (const line of pols) {
    if (!line.inventory_id) continue;
    const po = poById.get(line.purchase_order_id);
    if (!po) continue;
    const prev = best.get(line.inventory_id);
    if (!prev || po.order_date > prev.orderDate) {
      best.set(line.inventory_id, { orderDate: po.order_date, supplier_id: po.supplier_id });
    }
  }
  const out = new Map<string, string>();
  for (const [invId, v] of best) out.set(invId, v.supplier_id);
  return out;
}

export function computeReorderSuggestions(
  inventory: InvRow[],
  sales: SaleLine[],
  pos: PoRow[],
  deliveries: DelRow[],
  pols: PolRow[],
  suppliers: SupplierRow[],
  opts: { velocityDays: number; minUnitsPerWeek: number }
): ReorderRow[] {
  const since = Date.now() - opts.velocityDays * 24 * 60 * 60 * 1000;
  const unitsByItem = new Map<string, number>();
  for (const s of sales) {
    if (s.voided_at || !s.item_id) continue;
    if (new Date(s.created_at).getTime() < since) continue;
    unitsByItem.set(s.item_id, (unitsByItem.get(s.item_id) ?? 0) + Math.max(0, s.quantity ?? 0));
  }

  const skuSamples = buildSkuLeadSamples(pos, deliveries, pols);
  const invToSupplier = latestSupplierByInventoryId(pos, pols);
  const supDefaults = new Map(suppliers.map((s) => [s.id, s.default_lead_time_days]));

  const rows: ReorderRow[] = [];
  const weeks = opts.velocityDays / 7;
  for (const inv of inventory) {
    const unitsWindow = unitsByItem.get(inv.id) ?? 0;
    const unitsPerWeek = weeks > 0 ? unitsWindow / weeks : 0;
    if (unitsPerWeek < opts.minUnitsPerWeek) continue;
    const daily = unitsPerWeek / 7;
    const cover = daily > 0 ? inv.current_stock / daily : null;

    const hasPoLink = invToSupplier.has(inv.id);
    const supId = invToSupplier.get(inv.id);
    const hist = skuSamples.get(inv.id) ?? [];
    const def = supId ? supDefaults.get(supId) : undefined;
    const { days: leadDays, source } = resolveLeadTimeDays({
      historicalSamples: hist,
      supplierDefaultDays: def ?? null,
      hasPoLink,
    });

    const coverRounded = cover != null ? Math.max(0, Math.floor(cover)) : null;
    const stockLeftPhrase =
      coverRounded != null
        ? ` With ${inv.current_stock} units on hand, that is about ${coverRounded} days of stock left at this sales pace.`
        : ` With ${inv.current_stock} units on hand.`;
    const pacePhrase = `You sell about ${unitsPerWeek.toFixed(0)} units of ${inv.sku} per week.${stockLeftPhrase}`;

    let msg: string;
    let hasReorderAdvice = false;
    let isUrgent = false;

    if (source === "unknown" || leadDays == null) {
      msg = `${pacePhrase} ${REORDER_SETUP_CTA}`;
    } else {
      hasReorderAdvice = true;
      let leadPhrase: string;
      if (source === "historical_median") {
        leadPhrase = `Recent deliveries for this product took about ${leadDays} days.`;
      } else {
        leadPhrase = `Using the ${leadDays} delivery days you saved for this supplier (from your purchase order).`;
      }

      let action: string;
      if (cover != null && cover <= leadDays) {
        action = "You should order today.";
        isUrgent = true;
      } else if (cover != null && cover <= leadDays + 2) {
        action = "Order within the next day or two.";
        isUrgent = cover <= leadDays + 1;
      } else if (cover != null) {
        action = "No urgent reorder yet.";
      } else {
        action = "Review stock against weekly sales.";
      }

      msg = `${pacePhrase} ${leadPhrase} ${action}`;
    }

    rows.push({
      item_id: inv.id,
      sku: inv.sku,
      name: inv.name,
      current_stock: inv.current_stock,
      units_per_week: unitsPerWeek,
      days_of_cover: coverRounded,
      lead_days: leadDays,
      lead_source: source,
      has_reorder_advice: hasReorderAdvice,
      is_urgent: isUrgent,
      message: msg,
    });
  }

  rows.sort((a, b) => {
    const ac = a.days_of_cover ?? 999;
    const bc = b.days_of_cover ?? 999;
    return ac - bc;
  });
  return rows;
}
